import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useRef } from "react";
import { createEmbeddedPlayerHost } from "./host.js";
const INLINE_PLAY_DATA_CHAR_LIMIT = 12_000;
/**
 * Hosts the official Cocos standalone play renderer in an iframe and drives it
 * through the shared {@link createEmbeddedPlayerHost}. This is the React surface
 * of preview unification: any pane that renders `<EmbeddedPlayer>` shows the
 * real product renderer, not a reimplementation of it.
 */
export function EmbeddedPlayer({ playerUrl, episode, inlineEpisodeInUrl = true, episodeFiles, playerOrigin, playerHtml, playerBaseUrl, onEvent, onHostReady, className, title = "preview", }) {
    const iframeRef = useRef(null);
    const hostRef = useRef(null);
    // Keep the latest callbacks without forcing a host rebuild when they change.
    const onEventRef = useRef(onEvent);
    const onHostReadyRef = useRef(onHostReady);
    onEventRef.current = onEvent;
    onHostReadyRef.current = onHostReady;
    // Feed small compiled episodes inline so the player can start from boot data.
    // Full Preview payloads can be large enough to make webview iframe URLs
    // unreliable, so large episodes use the postMessage host.load() fallback only.
    const serializedEpisode = useMemo(() => {
        if (episode == null)
            return null;
        try {
            return JSON.stringify(episode);
        }
        catch {
            return null;
        }
    }, [episode]);
    const playerDocumentHtml = useMemo(() => (playerHtml ? playerHtmlWithBase(playerHtml, playerBaseUrl) : undefined), [playerBaseUrl, playerHtml]);
    const src = useMemo(() => {
        if (playerDocumentHtml) {
            return "about:blank";
        }
        if (!inlineEpisodeInUrl || !serializedEpisode || serializedEpisode.length > INLINE_PLAY_DATA_CHAR_LIMIT) {
            return playerUrl;
        }
        const sep = playerUrl.includes("?") ? "&" : "?";
        return `${playerUrl}${sep}playData=${encodeURIComponent(serializedEpisode)}`;
    }, [inlineEpisodeInUrl, playerDocumentHtml, playerUrl, serializedEpisode]);
    // (Re)build the control host whenever the iframe (re)loads — i.e. when `src`
    // changes (new bundle URL or small inline episode). The host also owns the
    // postMessage delivery fallback for large or cross-origin webview payloads.
    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe)
            return undefined;
        const host = createEmbeddedPlayerHost({
            iframe,
            playerOrigin,
            onEvent: (event) => onEventRef.current?.(event),
        });
        hostRef.current = host;
        host
            .ready()
            .then(() => onHostReadyRef.current?.(host))
            .catch(() => {
            /* ready-timeout / dispose — expected for a webview player, not surfaced */
        });
        return () => {
            host.dispose();
            hostRef.current = null;
        };
    }, [src, playerOrigin]);
    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe)
            return undefined;
        const eventSrc = () => iframe.getAttribute("src") ?? src;
        const handleLoad = () => {
            onEventRef.current?.({ event: "iframe-load", src: eventSrc() });
        };
        const handleError = () => {
            onEventRef.current?.({ event: "iframe-error", src: eventSrc() });
        };
        iframe.addEventListener("load", handleLoad);
        iframe.addEventListener("error", handleError);
        return () => {
            iframe.removeEventListener("load", handleLoad);
            iframe.removeEventListener("error", handleError);
        };
    }, [src]);
    useEffect(() => {
        if (!playerDocumentHtml)
            return;
        const iframe = iframeRef.current;
        const doc = iframe?.contentDocument;
        if (!doc) {
            onEventRef.current?.({ event: "error", stage: "iframe-document", message: "contentDocument unavailable" });
            return;
        }
        try {
            doc.open();
            doc.write(playerDocumentHtml);
            doc.close();
        }
        catch (error) {
            onEventRef.current?.({
                event: "error",
                stage: "iframe-document",
                message: error instanceof Error ? error.message : String(error),
            });
        }
    }, [playerDocumentHtml, src]);
    // Merge changed draft assets into the vendored (same-origin) player's virtual
    // FS before the episode (re)loads. Runs before the load effect below so the
    // player never re-fetches an asset ahead of its new bytes. The merge global
    // only exists after the player boots, so retry briefly until it appears.
    useEffect(() => {
        if (!playerDocumentHtml || !episodeFiles || Object.keys(episodeFiles).length === 0) {
            return undefined;
        }
        const iframe = iframeRef.current;
        if (!iframe)
            return undefined;
        const merge = () => {
            const win = iframe.contentWindow;
            if (win && typeof win.__LUNAVERSE_MERGE_PLAYER_FILES__ === "function") {
                win.__LUNAVERSE_MERGE_PLAYER_FILES__(episodeFiles);
                return true;
            }
            return false;
        };
        if (merge())
            return undefined;
        const poll = setInterval(() => {
            if (merge())
                clearInterval(poll);
        }, 100);
        const stop = setTimeout(() => clearInterval(poll), 15_000);
        return () => {
            clearInterval(poll);
            clearTimeout(stop);
        };
    }, [episodeFiles, playerDocumentHtml, src]);
    useEffect(() => {
        if (episode == null)
            return;
        hostRef.current?.load(episode);
    }, [episode, src]);
    return (_jsx("iframe", { ref: iframeRef, src: src, className: className, title: title, allow: "autoplay; fullscreen" }));
}
function playerHtmlWithBase(html, baseUrl) {
    if (!baseUrl)
        return html;
    const base = `<base href="${escapeHtmlAttr(baseUrl)}">`;
    const withBase = html.replace(/<head(\s[^>]*)?>/i, (match) => `${match}\n  ${base}`);
    return rewriteRelativePlayerUrls(withBase, baseUrl);
}
function rewriteRelativePlayerUrls(html, baseUrl) {
    return html
        .replace(/\b(src|href)="([^"]+)"/g, (match, attr, value) => {
        if (!shouldRewritePlayerUrl(value))
            return match;
        return `${attr}="${escapeHtmlAttr(joinPlayerUrl(baseUrl, value))}"`;
    })
        .replace(/System\.import\((['"])(\.\/[^'"]+)\1\)/g, (_match, quote, value) => {
        return `System.import(${quote}${joinPlayerUrl(baseUrl, value)}${quote})`;
    });
}
function shouldRewritePlayerUrl(value) {
    return !/^(?:[a-z][a-z0-9+.-]*:|\/\/|#)/i.test(value);
}
function joinPlayerUrl(baseUrl, value) {
    const base = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
    const relative = value.replace(/^\.\//, "");
    return `${base}${relative.replace(/^\/+/, "")}`;
}
function escapeHtmlAttr(value) {
    return value
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}
